import {type FilterConfig, type FilterProvider, type FilterQuery, FilterQueryParser} from '@github-ui/filter-core'

import {extractOrgsFromRawQuery, getValidOrgsFromProviders, parseFieldInput} from '../../utils/org-aware'
import {knownDashboardOrgs} from './issue-field-dashboard'

/**
 * Extended FilterQueryParser for org-aware field filtering in dashboard search.
 *
 * Fixes two issues when searching across multiple organizations:
 * 1. Routes field providers correctly based on org context (e.g., "org:github field.priority:")
 * 2. Transforms suggestions from "field.priority-github:" to clean "field.priority:" while auto-adding org context
 */
export class IssueFieldFilterQueryParser extends FilterQueryParser {
  /**
   * Creates an org-aware field filter parser that handles multi-org field routing.
   */
  constructor(filterProviders: FilterProvider[] = [], config?: FilterConfig) {
    super(filterProviders, config)
  }

  /**
   * Transform "field.priority-github:" to clean "field.priority:" for better UX.
   * Returns clean suggestion + metadata for automatic org context insertion.
   */
  #transformDashboardFieldSuggestion(
    suggestion: string,
    filterBarQuery: FilterQuery,
    _caretIndex: number,
  ): {transformedSuggestion: string; needsOrgInsertion: boolean; orgName: string} {
    // Get valid orgs from our field providers using shared utility and known dashboard orgs
    const providerKeys = this.filterProviders.map(p => p.key)
    const validOrgs = getValidOrgsFromProviders(providerKeys, knownDashboardOrgs)

    // Get the current org context from the query using shared utility
    const currentOrgs = extractOrgsFromRawQuery(filterBarQuery.raw)
    const allContextOrgs = new Set(currentOrgs)

    // Also check if there are repo filters that would indicate org context
    const repoMatches = filterBarQuery.raw.match(/repo:(?:"([^"]+)"|([^\s]+))/g)
    if (repoMatches) {
      for (const match of repoMatches) {
        const colonIndex = match.indexOf(':')
        const value = match.substring(colonIndex + 1)
        const repoValue = value.replace(/^"(.*)"$/, '$1')
        const orgFromRepo = repoValue.split('/')[0]
        if (orgFromRepo) allContextOrgs.add(orgFromRepo)
      }
    }

    // Parse the field suggestion to extract field name and org using shared utility
    const parseResult = parseFieldInput(suggestion, knownDashboardOrgs)
    if (!parseResult) {
      return {
        transformedSuggestion: suggestion,
        needsOrgInsertion: false,
        orgName: '',
      }
    }

    const {fieldName, orgName: potentialOrg} = parseResult

    // skip transformation if we already have an org/repo context in the query
    if (allContextOrgs.size > 0) {
      return {
        transformedSuggestion: suggestion,
        needsOrgInsertion: false,
        orgName: '',
      }
    }

    // Only transform if the potential org is a valid org (exists in our field providers)
    if (!validOrgs.has(potentialOrg)) {
      return {
        transformedSuggestion: suggestion,
        needsOrgInsertion: false,
        orgName: '',
      }
    }

    // Check if org is already in the current query context
    const orgAlreadyInContext = allContextOrgs.has(potentialOrg)

    // Keep the colon if the original suggestion had it
    const suggestionHasColon = suggestion.endsWith(':')
    return {
      transformedSuggestion: suggestionHasColon ? `field.${fieldName}:` : `field.${fieldName}`,
      needsOrgInsertion: !orgAlreadyInContext, // Only insert org if it's not already there
      orgName: potentialOrg,
    }
  }

  /**
   * Insert suggestion with automatic org context handling.
   * Transforms "field.priority-github:" to "org:github field.priority:" seamlessly.
   */
  override insertSuggestion(filterBarQuery: FilterQuery, suggestion: string, caretIndex: number): [string, number] {
    // Check if this is a dashboard field suggestion that needs transformation
    const {transformedSuggestion, needsOrgInsertion, orgName} = this.#transformDashboardFieldSuggestion(
      suggestion,
      filterBarQuery,
      caretIndex,
    )

    // If no transformation was applied, use the parent implementation with original suggestion
    if (transformedSuggestion === suggestion) {
      return super.insertSuggestion(filterBarQuery, suggestion, caretIndex)
    }

    // Always use the transformed suggestion, regardless of whether we need org insertion
    if (!needsOrgInsertion) {
      // Just insert the transformed field suggestion (org already exists in query)
      return super.insertSuggestion(filterBarQuery, transformedSuggestion, caretIndex)
    }

    // For org insertion, we need to be more careful about the placement
    // Instead of trying to detect where parent inserted, let's handle this directly

    // Check if org or repo filter already exists for this organization
    const existingOrgPattern = new RegExp(`\\borg:${orgName}\\b`)
    const existingRepoPattern = new RegExp(`\\brepo:(?:"${orgName}/|${orgName}/)`)
    if (existingOrgPattern.test(filterBarQuery.raw) || existingRepoPattern.test(filterBarQuery.raw)) {
      // Org or repo filter already exists, just insert the transformed field suggestion
      return super.insertSuggestion(filterBarQuery, transformedSuggestion, caretIndex)
    }

    // Insert both org filter and field suggestion together at the caret position
    const orgAndField = `org:${orgName} ${transformedSuggestion}`
    const [result, newCaretIndex] = super.insertSuggestion(filterBarQuery, orgAndField, caretIndex)

    return [result, newCaretIndex]
  }
}
